import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { apiClient } from "../../lib/http/apiClient";
import { env } from "../../config/env";
import { parseInstant } from "../../utils/dateUtils";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

function adminHeaders() {
  const token =
    localStorage.getItem("adminToken") ||
    localStorage.getItem("admin_token") ||
    localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const BASE = "/api/admin/subscriptions";

interface GatewayConfig {
  provider: string;
  enabled: boolean;
  razorpay_enabled?: boolean;
  stripe_enabled?: boolean;
  cashfree_enabled?: boolean;
  razorpay_key_id: string;
  has_razorpay_key_secret: boolean;
  has_razorpay_webhook_secret: boolean;
  stripe_publishable_key: string;
  has_stripe_secret_key: boolean;
  has_stripe_webhook_secret: boolean;
  stripe_success_url: string;
  stripe_cancel_url: string;
  cashfree_app_id?: string;
  has_cashfree_secret_key?: boolean;
  has_cashfree_webhook_secret?: boolean;
  cashfree_mode?: string;
  cashfree_return_url?: string;
  enabled_gateways?: string[];
  updated_at: string | null;
  updated_by: string | null;
}

interface PlanIds {
  seller_starter: string;
  seller_mid: string;
  seller_premium: string;
  buyer_pro: string;
  buyer_studio: string;
  buyer_elite: string;
}

const PLAN_LABELS: Record<keyof PlanIds, string> = {
  seller_starter: "Seller — Starter",
  seller_mid:     "Seller — Mid",
  seller_premium: "Seller — Premium",
  buyer_pro:      "Buyer — Pro",
  buyer_studio:   "Buyer — Studio",
  buyer_elite:    "Buyer — Elite",
};

type Tab = "credentials" | "razorpay-plans" | "stripe-plans" | "cashfree-plans";

interface CreatePriceForm {
  amount: string;       // rupees (user-facing), converted to paise on submit
  currency: string;
  interval: "month" | "year";
  product_name: string;
}

const DEFAULT_CREATE_FORM: CreatePriceForm = {
  amount: "", currency: "inr", interval: "year", product_name: "",
};

function SecretInput({ label, hasValue, name, value, onChange, placeholder }: {
  label: string;
  hasValue: boolean;
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">
        {label}
        {hasValue && !value && (
          <span className="ml-2 text-emerald-400 text-[10px] font-normal">✓ saved</span>
        )}
      </label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          name={name}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={hasValue ? "Leave blank to keep existing" : placeholder || "Enter value"}
          className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none placeholder-gray-600"
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
        >
          {show ? "🙈" : "👁"}
        </button>
      </div>
    </div>
  );
}

export default function AdminPaymentGatewayPage() {
  const [tab, setTab] = useState<Tab>("credentials");
  const [config, setConfig] = useState<GatewayConfig | null>(null);
  const [_planIds, setPlanIds] = useState<PlanIds | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const { showError, showSuccess } = useAdminToast();
  useEffect(() => {
    if (!msg) return;
    if (msg.type === "err") showError(msg.text);
    else showSuccess(msg.text);
  }, [msg, showError, showSuccess]);

  // credential form state
  const [form, setForm] = useState({
    provider: "razorpay",
    enabled: true,
    razorpay_enabled: false,
    stripe_enabled: false,
    cashfree_enabled: false,
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret: "",
    stripe_publishable_key: "",
    stripe_secret_key: "",
    stripe_webhook_secret: "",
    stripe_success_url: "",
    stripe_cancel_url: "",
    cashfree_app_id: "",
    cashfree_secret_key: "",
    cashfree_webhook_secret: "",
    cashfree_mode: "sandbox",
    cashfree_return_url: "",
  });

  // plan IDs form state
  const [planForm, setPlanForm] = useState<PlanIds>({
    seller_starter: "", seller_mid: "", seller_premium: "",
    buyer_pro: "", buyer_studio: "", buyer_elite: "",
  });

  // Stripe price IDs — stored values (read-only display)
  const [stripePriceForm, setStripePriceForm] = useState<PlanIds>({
    seller_starter: "", seller_mid: "", seller_premium: "",
    buyer_pro: "", buyer_studio: "", buyer_elite: "",
  });

  // Cashfree plan IDs
  const [cashfreePlanForm, setCashfreePlanForm] = useState<PlanIds>({
    seller_starter: "", seller_mid: "", seller_premium: "",
    buyer_pro: "", buyer_studio: "", buyer_elite: "",
  });

  // Create-price form state: one per plan key
  const [createForms, setCreateForms] = useState<Record<string, CreatePriceForm>>(
    Object.fromEntries(Object.keys(PLAN_LABELS).map(k => [k, { ...DEFAULT_CREATE_FORM }]))
  );
  const [creatingPlan, setCreatingPlan] = useState<string | null>(null);
  // Razorpay create-plan form state
  const [rzpCreateForms, setRzpCreateForms] = useState<Record<string, CreatePriceForm>>(
    Object.fromEntries(Object.keys(PLAN_LABELS).map(k => [k, { ...DEFAULT_CREATE_FORM, interval: "month" as "month" | "year" }]))
  );
  const [creatingRzpPlan, setCreatingRzpPlan] = useState<string | null>(null);
  // Cashfree create-plan form state
  const [cfCreateForms, setCfCreateForms] = useState<Record<string, CreatePriceForm>>(
    Object.fromEntries(Object.keys(PLAN_LABELS).map(k => [k, { ...DEFAULT_CREATE_FORM, interval: "month" as "month" | "year" }]))
  );
  const [creatingCfPlan, setCreatingCfPlan] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, ids, stripeIds, cashfreeIds] = await Promise.all([
        apiClient.get(`${BASE}/payment-gateway`, { headers: adminHeaders() }).then(r => r.data as GatewayConfig),
        apiClient.get(`${BASE}/payment-gateway/plan-ids`, { headers: adminHeaders() }).then(r => r.data as PlanIds),
        apiClient.get(`${BASE}/payment-gateway/stripe-price-ids`, { headers: adminHeaders() }).then(r => r.data as PlanIds),
        apiClient.get(`${BASE}/payment-gateway/cashfree-plan-ids`, { headers: adminHeaders() }).then(r => r.data as PlanIds).catch(() => ({
          seller_starter: "", seller_mid: "", seller_premium: "",
          buyer_pro: "", buyer_studio: "", buyer_elite: "",
        } as PlanIds)),
      ]);
      setConfig(cfg);
      setPlanIds(ids);
      setForm(f => ({
        ...f,
        provider: cfg.provider || "razorpay",
        enabled: cfg.enabled ?? true,
        razorpay_enabled: cfg.razorpay_enabled ?? false,
        stripe_enabled: cfg.stripe_enabled ?? false,
        cashfree_enabled: cfg.cashfree_enabled ?? false,
        razorpay_key_id: cfg.razorpay_key_id || "",
        stripe_publishable_key: cfg.stripe_publishable_key || "",
        stripe_success_url: cfg.stripe_success_url || "",
        stripe_cancel_url: cfg.stripe_cancel_url || "",
        cashfree_app_id: cfg.cashfree_app_id || "",
        cashfree_mode: cfg.cashfree_mode || "sandbox",
        cashfree_return_url: cfg.cashfree_return_url || "",
      }));
      setPlanForm({
        seller_starter: ids.seller_starter || "",
        seller_mid: ids.seller_mid || "",
        seller_premium: ids.seller_premium || "",
        buyer_pro: ids.buyer_pro || "",
        buyer_studio: ids.buyer_studio || "",
        buyer_elite: ids.buyer_elite || "",
      });
      setStripePriceForm({
        seller_starter: stripeIds.seller_starter || "",
        seller_mid: stripeIds.seller_mid || "",
        seller_premium: stripeIds.seller_premium || "",
        buyer_pro: stripeIds.buyer_pro || "",
        buyer_studio: stripeIds.buyer_studio || "",
        buyer_elite: stripeIds.buyer_elite || "",
      });
      setCashfreePlanForm({
        seller_starter: cashfreeIds.seller_starter || "",
        seller_mid: cashfreeIds.seller_mid || "",
        seller_premium: cashfreeIds.seller_premium || "",
        buyer_pro: cashfreeIds.buyer_pro || "",
        buyer_studio: cashfreeIds.buyer_studio || "",
        buyer_elite: cashfreeIds.buyer_elite || "",
      });
    } catch {
      setMsg({ type: "err", text: "Failed to load config" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function saveCredentials(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const res = await apiClient.put(`${BASE}/payment-gateway`, form, { headers: adminHeaders() });
      setConfig(res.data as GatewayConfig);
      setMsg({ type: "ok", text: "Gateway credentials saved." });
      setForm(f => ({ ...f, razorpay_key_secret: "", razorpay_webhook_secret: "", stripe_secret_key: "", stripe_webhook_secret: "", cashfree_secret_key: "", cashfree_webhook_secret: "" }));
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMsg({ type: "err", text: typeof d === "string" ? d : "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  async function savePlanIds(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const res = await apiClient.put(`${BASE}/payment-gateway/plan-ids`, planForm, { headers: adminHeaders() });
      setPlanIds(res.data as PlanIds);
      setMsg({ type: "ok", text: "Razorpay Plan IDs saved." });
    } catch {
      setMsg({ type: "err", text: "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  async function saveStripePriceIds(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await apiClient.put(`${BASE}/payment-gateway/stripe-price-ids`, stripePriceForm, { headers: adminHeaders() });
      setMsg({ type: "ok", text: "Stripe Price IDs saved." });
    } catch {
      setMsg({ type: "err", text: "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  async function saveCashfreePlanIds(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await apiClient.put(`${BASE}/payment-gateway/cashfree-plan-ids`, cashfreePlanForm, { headers: adminHeaders() });
      setMsg({ type: "ok", text: "Cashfree Plan IDs saved." });
    } catch {
      setMsg({ type: "err", text: "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  async function createPriceForPlan(planKey: string) {
    const cf = createForms[planKey];
    const amountRupees = parseFloat(cf.amount);
    if (!cf.amount || isNaN(amountRupees) || amountRupees <= 0) {
      setMsg({ type: "err", text: "Enter a valid amount > 0" });
      return;
    }
    setCreatingPlan(planKey);
    setMsg(null);
    try {
      const res = await apiClient.post(
        `${BASE}/payment-gateway/stripe-create-price`,
        {
          plan_key: planKey,
          amount: Math.round(amountRupees * 100), // rupees → paise
          currency: cf.currency,
          interval: cf.interval,
          product_name: cf.product_name || undefined,
        },
        { headers: adminHeaders() }
      );
      const d = res.data as { price_id: string; amount: number; currency: string; interval: string };
      setStripePriceForm(p => ({ ...p, [planKey]: d.price_id }));
      setMsg({
        type: "ok",
        text: `${PLAN_LABELS[planKey as keyof PlanIds]} → ${d.price_id} created (${d.currency.toUpperCase()} ${(d.amount / 100).toLocaleString("en-IN")}/${d.interval})`,
      });
      // Reset form for that plan
      setCreateForms(prev => ({ ...prev, [planKey]: { ...DEFAULT_CREATE_FORM } }));
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMsg({ type: "err", text: typeof d === "string" ? d : "Price creation failed" });
    } finally {
      setCreatingPlan(null);
    }
  }

  async function createRazorpayPlan(planKey: string) {
    const cf = rzpCreateForms[planKey];
    const amountRupees = parseFloat(cf.amount);
    if (!cf.amount || isNaN(amountRupees) || amountRupees <= 0) {
      setMsg({ type: "err", text: "Enter a valid amount > 0" });
      return;
    }
    setCreatingRzpPlan(planKey);
    setMsg(null);
    try {
      const res = await apiClient.post(
        `${BASE}/payment-gateway/razorpay-create-plan`,
        {
          plan_key: planKey,
          amount: Math.round(amountRupees * 100), // rupees → paise
          currency: "INR",
          interval: cf.interval === "year" ? "yearly" : "monthly",
          plan_name: cf.product_name || undefined,
        },
        { headers: adminHeaders() }
      );
      const d = res.data as { plan_id: string; amount: number; interval: string; plan_name: string };
      setPlanForm(p => ({ ...p, [planKey]: d.plan_id }));
      setMsg({
        type: "ok",
        text: `${PLAN_LABELS[planKey as keyof PlanIds]} → ${d.plan_id} created (₹${(d.amount / 100).toLocaleString("en-IN")}/${d.interval})`,
      });
      setRzpCreateForms(prev => ({ ...prev, [planKey]: { ...DEFAULT_CREATE_FORM, interval: "month" as "month" | "year" } }));
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMsg({ type: "err", text: typeof d === "string" ? d : "Razorpay plan creation failed" });
    } finally {
      setCreatingRzpPlan(null);
    }
  }

  async function createCashfreePlan(planKey: string) {
    const cf = cfCreateForms[planKey];
    const amountRupees = parseFloat(cf.amount);
    if (!cf.amount || isNaN(amountRupees) || amountRupees <= 0) {
      setMsg({ type: "err", text: "Enter a valid amount > 0" });
      return;
    }
    setCreatingCfPlan(planKey);
    setMsg(null);
    try {
      const res = await apiClient.post(
        `${BASE}/payment-gateway/cashfree-create-plan`,
        {
          plan_key: planKey,
          amount: Math.round(amountRupees * 100),
          currency: "INR",
          interval: cf.interval === "year" ? "YEAR" : "MONTH",
          plan_name: cf.product_name || undefined,
        },
        { headers: adminHeaders() }
      );
      const d = res.data as { plan_id: string; amount: number; interval: string; plan_name: string };
      setCashfreePlanForm(p => ({ ...p, [planKey]: d.plan_id }));
      setMsg({
        type: "ok",
        text: `${PLAN_LABELS[planKey as keyof PlanIds]} → ${d.plan_id} created (₹${(d.amount / 100).toLocaleString("en-IN")}/${d.interval})`,
      });
      setCfCreateForms(prev => ({ ...prev, [planKey]: { ...DEFAULT_CREATE_FORM, interval: "month" as "month" | "year" } }));
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMsg({ type: "err", text: typeof d === "string" ? d : "Cashfree plan creation failed" });
    } finally {
      setCreatingCfPlan(null);
    }
  }

  async function testConnection(provider: string) {
    setTesting(true);
    setMsg(null);
    try {
      const res = await apiClient.post(
        `${BASE}/payment-gateway/test?provider=${encodeURIComponent(provider)}`,
        {}, { headers: adminHeaders() });
      const d = res.data as { ok: boolean; provider: string; message: string };
      setMsg({ type: d.ok ? "ok" : "err", text: `${d.provider}: ${d.message}` });
    } catch {
      setMsg({ type: "err", text: "Test request failed" });
    } finally {
      setTesting(false);
    }
  }

  const f = (k: keyof typeof form, v: string | boolean) =>
    setForm(prev => ({ ...prev, [k]: v }));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div>
      <AdminPageHeader
        title="Payment Gateway"
        subtitle={
          <span className="text-sm">
            Gateway keys, mode and webhook. Plans are created automatically from Billing → Plans &amp; Prices.
            {config?.updated_at && (
              <span className="ml-2 text-gray-600">
                Last saved {(parseInstant(config.updated_at) ?? new Date(config.updated_at)).toLocaleString("en-IN")}
                {config.updated_by ? ` by ${config.updated_by}` : ""}
              </span>
            )}
          </span>
        }
        actions={<RefreshButton onRefresh={load} />}
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit max-w-full">
        {([
          ["credentials", "Gateway Credentials"],
          ["cashfree-plans", "Cashfree Plan IDs (legacy)"],
          ["razorpay-plans", "Razorpay Plan IDs (legacy)"],
          ["stripe-plans", "Stripe Price IDs (legacy)"],
        ] as [Tab, string][]).map(([t, label]) => (
          <button key={t} onClick={() => { setTab(t); setMsg(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium border ${
          msg.type === "ok"
            ? "bg-emerald-900/30 border-emerald-700 text-emerald-400"
            : "bg-red-900/30 border-red-700 text-red-400"
        }`}>
          {msg.text}
        </div>
      )}

      {/* Legacy plan-ID tabs: plans are created from Plans & Prices now. */}
      {tab !== "credentials" && (
        <div className="mb-4 max-w-2xl rounded-xl border border-amber-800/60 bg-amber-950/20 px-4 py-3 text-xs text-amber-200">
          Legacy. Plans are now created automatically when you save prices on{" "}
          <b>Billing → Plans &amp; Prices</b>, with readable names. These hand-entered IDs are only a
          fallback for monthly checkout when “Automatic Cashfree plans” is off.
        </div>
      )}

      {/* ── CREDENTIALS TAB ── */}
      {tab === "credentials" && (
        <form onSubmit={saveCredentials} className="space-y-6 max-w-2xl">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-2">
            <h2 className="text-sm font-semibold text-gray-300">Cashfree webhook URL</h2>
            <p className="text-xs text-gray-500">
              Add this in the Cashfree dashboard (Developers → Webhooks) with Payment and Subscription events.
              It must be a public URL — on localhost use your ngrok URL in place of the host.
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={`${(env.apiBaseUrl || window.location.origin).replace(/\/$/, "")}/api/v1/payments/cashfree/webhook`}
                className="flex-1 bg-gray-800 border border-gray-700 text-gray-200 text-xs font-mono rounded-lg px-3 py-2"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(
                  `${(env.apiBaseUrl || window.location.origin).replace(/\/$/, "")}/api/v1/payments/cashfree/webhook`,
                )}
                className="rounded-lg border border-gray-700 px-3 text-xs text-gray-300 hover:bg-gray-800"
              >
                Copy
              </button>
            </div>
          </div>
          {/* Provider + enabled */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-300">Provider Settings</h2>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-400 mb-1">Default Provider</label>
                <select value={form.provider} onChange={e => f("provider", e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="razorpay">Razorpay</option>
                  <option value="stripe">Stripe</option>
                  <option value="cashfree">Cashfree</option>
                  <option value="none">None (disabled)</option>
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.enabled}
                    onChange={e => f("enabled", e.target.checked)}
                    className="w-4 h-4 accent-indigo-500" />
                  <span className="text-sm text-gray-300">Payments Enabled</span>
                </label>
              </div>
            </div>
            {/* Hybrid selection: which gateways are live at checkout */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2">
                Live Gateways <span className="text-gray-600">(user picks among these at checkout)</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {([
                  ["razorpay_enabled", "Razorpay"],
                  ["stripe_enabled", "Stripe"],
                  ["cashfree_enabled", "Cashfree"],
                ] as [keyof typeof form, string][]).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={Boolean(form[k])}
                      onChange={e => f(k, e.target.checked)}
                      className="w-4 h-4 accent-indigo-500" />
                    <span className="text-sm text-gray-300">{label}</span>
                  </label>
                ))}
              </div>
              {config?.enabled_gateways && config.enabled_gateways.length > 0 && (
                <p className="text-[11px] text-gray-600 mt-2">
                  Currently live: {config.enabled_gateways.join(", ")}
                </p>
              )}
            </div>
          </div>

          {/* Razorpay */}
          {(form.provider === "razorpay" || form.razorpay_enabled) && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-gray-300">Razorpay Credentials</h2>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Key ID <span className="text-gray-600">(public)</span></label>
                <input value={form.razorpay_key_id} onChange={e => f("razorpay_key_id", e.target.value)}
                  placeholder="rzp_live_... or rzp_test_..."
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
              </div>
              <SecretInput label="Key Secret" hasValue={config?.has_razorpay_key_secret ?? false}
                name="razorpay_key_secret" value={form.razorpay_key_secret}
                onChange={v => f("razorpay_key_secret", v)} placeholder="rzp_secret_..." />
              <SecretInput label="Webhook Secret" hasValue={config?.has_razorpay_webhook_secret ?? false}
                name="razorpay_webhook_secret" value={form.razorpay_webhook_secret}
                onChange={v => f("razorpay_webhook_secret", v)} placeholder="whsec_..." />
              <div className="pt-1">
                <button type="button" onClick={() => testConnection("razorpay")} disabled={testing || !form.razorpay_key_id}
                  className="px-4 py-1.5 text-xs font-semibold border border-indigo-500 text-indigo-400 rounded-lg hover:bg-indigo-500/10 disabled:opacity-50 transition-colors">
                  {testing ? "Testing…" : "Test Connection"}
                </button>
                <span className="ml-2 text-[11px] text-gray-600">Validates key_id + key_secret via Razorpay API (read-only)</span>
              </div>
            </div>
          )}

          {/* Stripe */}
          {(form.provider === "stripe" || form.stripe_enabled) && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-gray-300">Stripe Credentials</h2>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Publishable Key <span className="text-gray-600">(public)</span></label>
                <input value={form.stripe_publishable_key} onChange={e => f("stripe_publishable_key", e.target.value)}
                  placeholder="pk_live_... or pk_test_..."
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
              </div>
              <SecretInput label="Secret Key" hasValue={config?.has_stripe_secret_key ?? false}
                name="stripe_secret_key" value={form.stripe_secret_key}
                onChange={v => f("stripe_secret_key", v)} placeholder="sk_live_... or sk_test_..." />
              <SecretInput label="Webhook Secret" hasValue={config?.has_stripe_webhook_secret ?? false}
                name="stripe_webhook_secret" value={form.stripe_webhook_secret}
                onChange={v => f("stripe_webhook_secret", v)} placeholder="whsec_..." />
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Success Redirect URL</label>
                <input value={form.stripe_success_url} onChange={e => f("stripe_success_url", e.target.value)}
                  placeholder="https://yourdomain.com/payment/success"
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Cancel Redirect URL</label>
                <input value={form.stripe_cancel_url} onChange={e => f("stripe_cancel_url", e.target.value)}
                  placeholder="https://yourdomain.com/pricing"
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
              </div>
              <div className="pt-1">
                <button type="button" onClick={() => testConnection("stripe")} disabled={testing}
                  className="px-4 py-1.5 text-xs font-semibold border border-indigo-500 text-indigo-400 rounded-lg hover:bg-indigo-500/10 disabled:opacity-50 transition-colors">
                  {testing ? "Testing…" : "Test Connection"}
                </button>
                <span className="ml-2 text-[11px] text-gray-600">Validates secret key via Stripe Balance API (read-only)</span>
              </div>
            </div>
          )}

          {/* Cashfree */}
          {(form.provider === "cashfree" || form.cashfree_enabled) && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-gray-300">Cashfree Credentials</h2>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">App ID <span className="text-gray-600">(x-client-id)</span></label>
                <input value={form.cashfree_app_id} onChange={e => f("cashfree_app_id", e.target.value)}
                  placeholder="CF..."
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
              </div>
              <SecretInput label="Secret Key" hasValue={config?.has_cashfree_secret_key ?? false}
                name="cashfree_secret_key" value={form.cashfree_secret_key}
                onChange={v => f("cashfree_secret_key", v)} placeholder="cfsk_..." />
              <SecretInput label="Webhook Secret" hasValue={config?.has_cashfree_webhook_secret ?? false}
                name="cashfree_webhook_secret" value={form.cashfree_webhook_secret}
                onChange={v => f("cashfree_webhook_secret", v)} placeholder="Leave blank to reuse secret key" />
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-400 mb-1">Mode</label>
                  <select value={form.cashfree_mode} onChange={e => f("cashfree_mode", e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="sandbox">Sandbox (test)</option>
                    <option value="production">Production (live)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Return / Redirect URL</label>
                <input value={form.cashfree_return_url} onChange={e => f("cashfree_return_url", e.target.value)}
                  placeholder="https://yourdomain.com/payment/success"
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
              </div>
              <p className="text-[11px] text-gray-600">
                Webhook URL: <code className="text-gray-400">/api/v1/payments/cashfree/webhook</code>
              </p>
              <div className="pt-1">
                <button type="button" onClick={() => testConnection("cashfree")} disabled={testing || !form.cashfree_app_id}
                  className="px-4 py-1.5 text-xs font-semibold border border-indigo-500 text-indigo-400 rounded-lg hover:bg-indigo-500/10 disabled:opacity-50 transition-colors">
                  {testing ? "Testing…" : "Test Connection"}
                </button>
                <span className="ml-2 text-[11px] text-gray-600">Validates app_id + secret key via Cashfree API (read-only)</span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving}
              className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors">
              {saving ? "Saving…" : "Save Credentials"}
            </button>
            <p className="text-xs text-gray-600">
              Secret fields left blank are preserved from last save.
            </p>
          </div>
        </form>
      )}

      {/* ── RAZORPAY PLAN IDS TAB ── */}
      {tab === "razorpay-plans" && (
        <div className="space-y-4 max-w-2xl">
          <p className="text-xs text-gray-500">
            Set amount + billing interval per plan. Backend creates the Razorpay plan via API and stores the ID automatically — no Razorpay dashboard needed.
          </p>

          {(Object.keys(PLAN_LABELS) as (keyof PlanIds)[]).map(key => {
            const cf = rzpCreateForms[key];
            const currentId = planForm[key];
            const isCreating = creatingRzpPlan === key;
            return (
              <div key={key} className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-300">{PLAN_LABELS[key]}</h3>
                  {currentId && (
                    <span className="text-[11px] text-emerald-400 font-mono truncate max-w-[200px]" title={currentId}>
                      ✓ {currentId}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Amount (₹)</label>
                    <input
                      type="number" min="1" step="0.01"
                      value={cf.amount}
                      onChange={e => setRzpCreateForms(p => ({ ...p, [key]: { ...p[key], amount: e.target.value } }))}
                      placeholder="e.g. 999"
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Billing Interval</label>
                    <select
                      value={cf.interval}
                      onChange={e => setRzpCreateForms(p => ({ ...p, [key]: { ...p[key], interval: e.target.value as "month" | "year" } }))}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Display Name (optional)</label>
                    <input
                      value={cf.product_name}
                      onChange={e => setRzpCreateForms(p => ({ ...p, [key]: { ...p[key], product_name: e.target.value } }))}
                      placeholder={key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => createRazorpayPlan(key)}
                  disabled={isCreating || !cf.amount}
                  className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {isCreating ? "Creating…" : currentId ? "Create New Plan" : "Create & Save Plan"}
                </button>
              </div>
            );
          })}

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-3 font-semibold">Advanced: paste existing Razorpay Plan ID directly</p>
            <form onSubmit={savePlanIds} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(Object.keys(PLAN_LABELS) as (keyof PlanIds)[]).map(key => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{PLAN_LABELS[key]}</label>
                    <input value={planForm[key]}
                      onChange={e => setPlanForm(p => ({ ...p, [key]: e.target.value }))}
                      placeholder="plan_..."
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
                  </div>
                ))}
              </div>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-gray-700 text-white text-xs font-semibold rounded-lg hover:bg-gray-600 disabled:opacity-60 transition-colors">
                {saving ? "Saving…" : "Save IDs Manually"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── STRIPE PRICE IDS TAB ── */}
      {tab === "stripe-plans" && (
        <div className="space-y-4 max-w-2xl">
          <p className="text-xs text-gray-500">
            Set amount + billing interval per plan. Backend creates a Stripe Price object and stores the ID automatically — no Stripe dashboard needed.
          </p>

          {(Object.keys(PLAN_LABELS) as (keyof PlanIds)[]).map(key => {
            const cf = createForms[key];
            const currentId = stripePriceForm[key];
            const isCreating = creatingPlan === key;
            return (
              <div key={key} className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-300">{PLAN_LABELS[key]}</h3>
                  {currentId && (
                    <span className="text-[11px] text-emerald-400 font-mono truncate max-w-[200px]" title={currentId}>
                      ✓ {currentId}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Amount (₹)</label>
                    <input
                      type="number" min="1" step="0.01"
                      value={cf.amount}
                      onChange={e => setCreateForms(p => ({ ...p, [key]: { ...p[key], amount: e.target.value } }))}
                      placeholder="e.g. 2000"
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Billing Interval</label>
                    <select
                      value={cf.interval}
                      onChange={e => setCreateForms(p => ({ ...p, [key]: { ...p[key], interval: e.target.value as "month" | "year" } }))}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Currency</label>
                    <select
                      value={cf.currency}
                      onChange={e => setCreateForms(p => ({ ...p, [key]: { ...p[key], currency: e.target.value } }))}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      <option value="inr">INR (₹)</option>
                      <option value="usd">USD ($)</option>
                      <option value="eur">EUR (€)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Display Name (optional)</label>
                    <input
                      value={cf.product_name}
                      onChange={e => setCreateForms(p => ({ ...p, [key]: { ...p[key], product_name: e.target.value } }))}
                      placeholder={key.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => createPriceForPlan(key)}
                  disabled={isCreating || !cf.amount}
                  className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {isCreating ? "Creating…" : currentId ? "Create New Price" : "Create & Save Price"}
                </button>
              </div>
            );
          })}

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-3 font-semibold">Advanced: paste existing Stripe Price ID directly</p>
            <form onSubmit={saveStripePriceIds} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(Object.keys(PLAN_LABELS) as (keyof PlanIds)[]).map(key => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{PLAN_LABELS[key]}</label>
                    <input value={stripePriceForm[key]}
                      onChange={e => setStripePriceForm(p => ({ ...p, [key]: e.target.value }))}
                      placeholder="price_..."
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
                  </div>
                ))}
              </div>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-gray-700 text-white text-xs font-semibold rounded-lg hover:bg-gray-600 disabled:opacity-60 transition-colors">
                {saving ? "Saving…" : "Save IDs Manually"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── CASHFREE PLAN IDS TAB ── */}
      {tab === "cashfree-plans" && (
        <div className="space-y-4 max-w-2xl">
          <p className="text-xs text-gray-500">
            Set amount + billing interval per plan. Backend creates the Cashfree plan via API and stores the ID automatically — no Cashfree dashboard needed.
          </p>

          {(Object.keys(PLAN_LABELS) as (keyof PlanIds)[]).map(key => {
            const cf = cfCreateForms[key];
            const currentId = cashfreePlanForm[key];
            const isCreating = creatingCfPlan === key;
            return (
              <div key={key} className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-300">{PLAN_LABELS[key]}</h3>
                  {currentId && (
                    <span className="text-[11px] text-emerald-400 font-mono truncate max-w-[200px]" title={currentId}>
                      ✓ {currentId}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Amount (₹)</label>
                    <input
                      type="number" min="1" step="0.01"
                      value={cf.amount}
                      onChange={e => setCfCreateForms(p => ({ ...p, [key]: { ...p[key], amount: e.target.value } }))}
                      placeholder="e.g. 999"
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Billing Interval</label>
                    <select
                      value={cf.interval}
                      onChange={e => setCfCreateForms(p => ({ ...p, [key]: { ...p[key], interval: e.target.value as "month" | "year" } }))}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Display Name (optional)</label>
                    <input
                      value={cf.product_name}
                      onChange={e => setCfCreateForms(p => ({ ...p, [key]: { ...p[key], product_name: e.target.value } }))}
                      placeholder={key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => createCashfreePlan(key)}
                  disabled={isCreating || !cf.amount}
                  className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {isCreating ? "Creating…" : currentId ? "Create New Plan" : "Create & Save Plan"}
                </button>
              </div>
            );
          })}

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-3 font-semibold">Advanced: paste existing Cashfree Plan ID directly</p>
            <form onSubmit={saveCashfreePlanIds} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(Object.keys(PLAN_LABELS) as (keyof PlanIds)[]).map(key => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{PLAN_LABELS[key]}</label>
                    <input value={cashfreePlanForm[key]}
                      onChange={e => setCashfreePlanForm(p => ({ ...p, [key]: e.target.value }))}
                      placeholder="plan_..."
                      className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none placeholder-gray-600" />
                  </div>
                ))}
              </div>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-gray-700 text-white text-xs font-semibold rounded-lg hover:bg-gray-600 disabled:opacity-60 transition-colors">
                {saving ? "Saving…" : "Save IDs Manually"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="mt-8 max-w-2xl bg-amber-900/20 border border-amber-800/40 rounded-xl p-4">
        <p className="text-xs text-amber-400 font-semibold mb-1">Security reminder</p>
        <ul className="text-xs text-amber-500/80 space-y-1 list-disc list-inside">
          <li>Secret keys are never returned to this UI — only a "✓ saved" indicator is shown.</li>
          <li>DB credentials take priority over <code>RAZORPAY_KEY_*</code> env vars at runtime.</li>
          <li>Use Razorpay test keys (<code>rzp_test_*</code>) in staging, live keys in production only.</li>
          <li>Rotate keys here; changes take effect within 2 minutes (cache TTL).</li>
        </ul>
      </div>
    </div>
  );
}
